import * as _ from 'lodash';
import { ActionFunctionAny, createAction, handleActions } from 'redux-actions';
import { IStoreState } from './store/StoreState';

export interface IDispatch {
  <T extends (...params: any[]) => TResult, TResult>(action): TResult;
}
/**
 * action for a specific method decorator
 * @param actionName the name of the action to use
 */
export function action(actionName?: string) {
  return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
    const innerActionFunc = target[propertyKey];
    const innerAction = (...args) => innerActionFunc.apply(target, args);
    target[propertyKey] = createAction(actionName || propertyKey, innerAction);
  };
}

const aggregateActionKey = 'aggregateActionKey';
export type IAggregateActionParams = (
  dispatch: (action: Promise<any>) => Promise<any>,
  getState?: () => IStoreState,
) => Promise<void>;

/**
 * Decorate with this class to declare an AggregateAction.
 * AggregateActions will not publish events of their own and should not be mapped to a reducer.
 */
export function aggregateAction() {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    defineMeta(aggregateActionKey, true, target, propertyKey);
  };
}

export type IErrorPassthroughAction = (state: any, action: IAction<any>) => any;
export type IErrorClass<T, K extends keyof T> = T & { [P in K]: IErrorPassthroughAction };

export function applyDispatch<T extends Function, TDispatch extends IDispatch>(
  dispatch: TDispatch,
  action: T,
  target?: object,
): T {
  return ((...args: any[]) => dispatch(action.apply(target, args))) as any as T;
}

/**
 * Decorate this class to apply the action decorator to all methods
 */
export type Constructor<T> = new (...args: any[]) => T;
export function actionClass<T>(constructorFunc: Constructor<T>): Constructor<actionClassType<T>> {
  for (const key in constructorFunc.prototype) {
    if (typeof constructorFunc[key] === 'function') {
      action()(constructorFunc, key, undefined);
    }
  }
  return class extends (constructorFunc as Constructor<{}>) {
    constructor(...args: any[]) {
      super(...args);

      for (const key in this) {
        if (typeof this[key] === 'function' && key !== 'constructor') {
          const metadata = getMeta(aggregateActionKey, this, key);
          if (!metadata) {
            // the @actionType() can be used on an action to explicitly set the action type
            // else we just use the name of the action
            const explicitActionType = getMeta(actionTypeKey, this, key);
            action(explicitActionType)(this, key, undefined);
          }
        }
      }
    }
  } as Constructor<actionClassType<T>>;
}

const actionTypeKey = 'actionType';

export function actionType(value: string) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    defineMeta(actionTypeKey, value, target, propertyKey);
  };
}

const noReducerKey = 'noReducer';

export function noReducer() {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    defineMeta(noReducerKey, true, target, propertyKey);
  };
}

export type actionClassType<TOriginal> = { [k in keyof TOriginal]: ActionFunctionAny<any> };

export type IPayload<T> = T;

export type IAction<T> = ISuccessfulAction<T> | IUnsuccessfulAction;

export type ISuccessfulAction<T> = {
  payload: IPayload<T>;
  error?: false | undefined;
};

export type IUnsuccessfulAction = {
  payload: string;
  error: true;
};

export type Reducer<TActions, TState> = {
  // defaultState: TState;
  [K in keyof TActions]?: (state: TState, action: IAction<any>) => TState;
};

/**
 * This creates a reducer
 * @param actions the actions object to create the reducer from
 * @param reducer
 * @param defaultState
 */
export function createReducer<TActions, TState>(actions: TActions, reducer: Reducer<TActions, TState>, defaultState: TState) {
  const mapValues = _.keys(actions)
    .filter(
      // filter actions that explicitly should not have a reducer
      (k) => !getMeta(noReducerKey, actions, actions[k].toString()),
    )
    .map((k) => {
      let actionName = actions[k].toString();

      // the @actionType() can be used on an action to explicitly set the action type
      // else we just use the name of the action
      const explicitActionType = getMeta(actionTypeKey, actions, actionName);
      const actionType = explicitActionType || actionName;

      return {
        [actionType](state: TState, action: IAction<any>): TState {
          // we MUST use the reducer return from the map DIRECTLY in order to
          // keep the *this* pointer associated with it.
          if (!reducer[k as keyof TActions]) {
            throw new Error(`failed to find reducer ${k}`);
          }

          return reducer[k as keyof TActions](state, action);
        },
      };
    });
  const map = {};
  mapValues.forEach((m) => _.extend(map, m));

  return handleActions(map, defaultState);
}

const meta = 'meta';
const defineMeta = (metadataKey: string, metadataValue: any, target: Object, propertyKey: string | symbol) => {
  if (!!target?.[propertyKey]) {
    target[propertyKey].meta = { ...(target[propertyKey].meta ?? {}), [metadataKey]: metadataValue };
  }
};

const getMeta = (metadataKey: any, target: Object, propertyKey: string | symbol) => {
  return _.get(target, [propertyKey, meta, metadataKey], undefined);
};
